import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useCrosschainBuy } from './useCrosschainBuy'
import { useWallet } from '@/features/ton-wallet'
import { refreshRecentSwaps } from '@/features/crosschain-buy/recent-swaps'
import { getBuyFlowController } from '@/features/crosschain-buy/buy-flow/controller'
import { ChainId, FlowStatus } from '@/features/crosschain-buy/domain/flow'
import { useDebugLogPanel } from '../sections/useDebugLogPanel'
import { useAmountInput } from '../../../source-tokens'
import { useWalletHandlers } from './useWalletHandlers'
import { useWallets } from '../../../wallets/use-wallets'
import { usePhantom } from '../../../wallets/phantom-deeplink/use-phantom'
import { useBuySpottyCopy } from './useBuySpottyCopy'
import { useBuySpottyHandlers } from './useBuySpottyHandlers'
import { PhantomLaunchFailureWatchdog } from '../../../wallets/phantom-deeplink/launch-watchdog'
import { isFullyCompletedExecution } from '../../presentation/start-new-swap-guard'
import { createChainSelectionHandler } from './chain-selection'
import {
  createCompletedExecutionEditCoordinator,
  createCompletedExecutionRouteHandlers,
} from './completed-execution-edit'

export interface BuySpottyContentProps {
  variant: 'page' | 'modal'
  externalActionButton: boolean
}

export const useBuySpottyContentPresenter = (props: BuySpottyContentProps) => {
  const controller = getBuyFlowController()
  const buy = useCrosschainBuy()
  const wallets = useWallets()
  const phantom = usePhantom()
  const launchWatchdog = new PhantomLaunchFailureWatchdog({
    isVisible: () => document.visibilityState === 'visible',
    delayMs: 1_500,
  })
  onUnmounted(() => {
    launchWatchdog.dispose()
  })
  const {
    sourceChainOptions,
    selectedChainId,
    selectedSourceTokenAddress,
    sourceAmount,
    sourceChainType,
    availableSourceTokens,
    isLoadingTokens,
    tokenLoadBlockedMessage,
    routeUxStatus,
    state,
    solanaBridgeProvider,
    invalidateRouteContext,
    canCancelExecution,
    canAbandonExecution,
    routeLocked,
    networkSelectionLocked,
  } = buy
  const tonWallet = useWallet()
  const uiError = ref('')

  watch(
    [
      () => state.value.evm.address,
      () => state.value.solana.address,
      () => state.value.ton.address,
    ],
    ([newEvm, newSol, newTon], [oldEvm, oldSol, oldTon]) => {
      const newlyConnected = (!oldEvm && newEvm) || (!oldSol && newSol) || (!oldTon && newTon)
      if (newlyConnected) uiError.value = ''
    }
  )
  onMounted(() => {
    uiError.value = ''
    launchWatchdog.clear()
  })
  watch(
    () => state.value.status,
    (next, prev) => {
      if (prev === FlowStatus.ERROR && next !== FlowStatus.ERROR) uiError.value = ''
    }
  )

  const { debugLog, debugCopyStatus, copyDebugLog } = useDebugLogPanel()
  const phantomDeeplinkAvailable = computed(() => phantom.isDeeplinkEnvironment())
  const walletHandlers = useWalletHandlers({
    uiError,
    sourceChainType,
    phantomDeeplinkAvailable,
    tonAddress: tonWallet.address,
    connectEvmWallet: wallets.connectEvm,
    disconnectEvmWallet: wallets.disconnectEvm,
    connectSolanaWallet: wallets.connectSolana,
    connectPhantomDeeplink: wallets.connectPhantom,
    disconnectSolanaWallet: wallets.disconnectSolana,
    connectTonWallet: wallets.connectTon,
    disconnectTonWallet: wallets.disconnectTon,
    connectTon: tonWallet.connect,
    recheckTonBackendSession: tonWallet.recheckBackendSession,
    reconnectTonForFreshProof: tonWallet.reconnectForFreshProof,
    disconnectTon: tonWallet.disconnect,
    isTonBackendConnected: tonWallet.isAuthenticated,
    hasActiveTonConnectSession: tonWallet.hasActiveTonConnectSession,
    recordDiagnostic: controller.recordDiagnostic,
  })

  const needsTonBackendReauth = computed(
    () =>
      tonWallet.isTonConnectRestoreSettled.value &&
      !tonWallet.isBackendAuthRestoring.value &&
      tonWallet.hasActiveTonConnectSession.value &&
      !tonWallet.isAuthenticated.value &&
      !!state.value.ton.address
  )
  const cardClass = computed(() => (props.variant === 'page' ? 'bg-white shadow-sm' : 'bg-gray-50'))
  const disconnectBtnClass = computed(() =>
    props.variant === 'page' ? 'border-gray-300 bg-gray-50 hover:bg-gray-100' : 'border-gray-200'
  )
  const promptPaddingClass = computed(() => (props.variant === 'page' ? 'py-6' : 'py-4'))
  const inputBgClass = computed(() => (props.variant === 'page' ? 'bg-gray-50' : 'bg-white'))
  const sourceWalletAddress = computed(() =>
    sourceChainType.value === 'solana' ? state.value.solana.address : state.value.evm.address
  )
  const EVM_CHAIN_LABELS: Readonly<Record<number, string>> = {
    [ChainId.ETHEREUM]: 'Ethereum',
    [ChainId.OPTIMISM]: 'Optimism',
    [ChainId.ARBITRUM]: 'Arbitrum',
    [ChainId.BASE]: 'Base',
  }
  const evmChainMismatch = computed(() => {
    if (sourceChainType.value !== 'evm' || !state.value.evm.address) return null
    const actualChainId = state.value.evm.chainId
    if (actualChainId === selectedChainId.value) return null
    return {
      actualLabel:
        actualChainId === null
          ? 'an unverified network'
          : (EVM_CHAIN_LABELS[actualChainId] ?? `chain ${String(actualChainId)}`),
      targetLabel:
        EVM_CHAIN_LABELS[selectedChainId.value] ?? `chain ${String(selectedChainId.value)}`,
    }
  })
  const maskedTonAddress = computed(() => {
    const address = state.value.ton.address
    return !address || address.length < 10 ? address : `${address.slice(0, 6)}…${address.slice(-4)}`
  })

  const normalizeAddress = (address: string): string =>
    selectedChainId.value === ChainId.SOLANA ? address : address.toLowerCase()
  const selectedToken = computed(() => {
    const selected = selectedSourceTokenAddress.value
    if (selected === undefined) return undefined
    return availableSourceTokens.value.find(
      (token) => normalizeAddress(token.address) === normalizeAddress(selected)
    )
  })
  const amount = useAmountInput({
    sourceAmount,
    selectedToken,
    selectedChainId,
    isLoadingTokens,
    selectSourceToken: buy.selectSourceToken,
    setSourceAmount: buy.setSourceAmount,
    invalidateRouteContext,
    routeLocked,
  })

  const actions = useBuySpottyHandlers({
    uiError,
    state,
    needsTonReconnect: wallets.needsTonReconnect,
    needsTonBackendReauth,
    sourceWalletAddress,
    sourceChainType,
    solanaBridgeProvider,
    runContinue: buy.runContinue,
    cancelExecution: buy.endExecution,
    refreshRecentSwaps,
    debouncedRequote: buy.debouncedRequote,
    handleConnectSource: () => walletHandlers.handleConnectSource('primary'),
    handleConnectTon: walletHandlers.handleConnectTon,
    recordDiagnostic: controller.recordDiagnostic,
    isPhantomDeeplinkSuspended: phantom.isSuspendedError,
    routeLocked,
    evmChainMismatch,
    handleSwitchEvmChain: () => wallets.switchEvm(selectedChainId.value),
  })
  const completedEditCoordinator = createCompletedExecutionEditCoordinator({
    routeLocked: () => routeLocked.value,
    completedExecution: () => isFullyCompletedExecution(state.value),
    endCompletedExecution: () => actions.handleCancel(true),
    reportCompletedExitFailure: () => {
      uiError.value = 'Could not start a new swap. Please try again.'
    },
  })
  const selectChain = createChainSelectionHandler({
    selectedChainId: () => selectedChainId.value,
    applyEdit: completedEditCoordinator.apply,
    selectSourceChain: buy.selectSourceChain,
    invalidateRouteContext,
    activateSolana: wallets.activateSolana,
  })
  const completedRouteHandlers = createCompletedExecutionRouteHandlers(completedEditCoordinator, {
    selectToken: amount.selectToken,
    handleAmountInput: amount.handleAmountInput,
    setMaxAmount: amount.setMaxAmount,
    applyQuickAmount: amount.applyQuickAmount,
  })
  const routeEditLocked = computed(() => networkSelectionLocked.value || actions.isFlowBusy.value)
  const copy = useBuySpottyCopy({
    state,
    needsTonReconnect: wallets.needsTonReconnect,
    needsTonBackendReauth,
    routeUxStatus,
    sourceWalletAddress,
    selectedToken,
    sourceAmount,
    amountValidationError: amount.amountValidationError,
    spendableBalanceLabel: amount.spendableBalanceLabel,
    sourceChainType,
    selectedSourceTokenAddress,
    availableSourceTokens,
    isFlowBusy: actions.isFlowBusy,
    evmChainMismatch,
  })
  const handoffPhase = computed(() => phantom.bridgeHandoffState.value.phase)
  watch(handoffPhase, (phase) => {
    if (phase !== 'opening') launchWatchdog.clear()
  })
  const showPhantomBridgeHandoff = computed(
    () =>
      handoffPhase.value !== 'idle' &&
      (state.value.status === FlowStatus.SOLANA_JUPITER_SWAPPING ||
        state.value.status === FlowStatus.SOLANA_LAYERSWAP_BRIDGING)
  )
  const handoffLabel = computed(() => {
    return phantom.canTriggerBridgeHandoff() ? 'Continue' : 'Processing...'
  })
  const handlePhantomBridgeHandoff = async (): Promise<void> => {
    uiError.value = ''
    if (handoffPhase.value === 'error') {
      if (!phantom.bridgeHandoffState.value.retryable) return
      try {
        await phantom.retryBridgeHandoffPreparation()
      } catch {
        uiError.value = phantom.bridgeHandoffState.value.message ?? 'Bridge preparation failed.'
      }
      return
    }
    if (handoffPhase.value !== 'ready') return
    try {
      await phantom.launchPreparedBridgeHandoff()
    } catch (error) {
      if (phantom.isSuspendedError(error)) {
        launchWatchdog.arm(() => {
          phantom.markBridgeHandoffLaunchFailed(true)
        })
        return
      }
      uiError.value = 'Phantom did not open.'
    }
  }
  const showPhantomBridgeFallback = computed(
    () => showPhantomBridgeHandoff.value && phantom.canLaunchBridgeHandoffFallback()
  )
  const handlePhantomBridgeFallback = async (): Promise<void> => {
    if (!showPhantomBridgeFallback.value) return
    uiError.value = ''
    try {
      await phantom.launchBridgeHandoffFallback()
    } catch (error) {
      if (phantom.isSuspendedError(error)) {
        launchWatchdog.arm(() => {
          phantom.markBridgeHandoffLaunchFailed()
        })
        return
      }
      uiError.value = 'Phantom did not open.'
    }
  }
  const primaryContinueLabel = computed(() =>
    showPhantomBridgeHandoff.value ? handoffLabel.value : copy.continueLabel.value
  )
  const primaryBusy = computed(
    () =>
      actions.isFlowBusy.value ||
      handoffPhase.value === 'preparing' ||
      handoffPhase.value === 'opening'
  )
  const primaryDisabled = computed(() =>
    showPhantomBridgeHandoff.value
      ? !phantom.canTriggerBridgeHandoff()
      : copy.isContinueDisabled.value
  )
  const handlePrimaryContinue = (): Promise<void> =>
    showPhantomBridgeHandoff.value ? handlePhantomBridgeHandoff() : actions.handleContinue()

  watch(
    [copy.friendlyErrorMessage, uiError],
    ([friendlyErrorMessage, localError]) => {
      if (!friendlyErrorMessage && !localError) return
      controller.recordDiagnostic('banner.source', {
        friendlyErrorMessage,
        uiError: localError,
        status: state.value.status,
        lastError: state.value.lastError,
        evmAddrPresent: !!state.value.evm.address,
        solanaAddrPresent: !!state.value.solana.address,
        hasQuote: !!state.value.quote,
      })
    },
    { immediate: true }
  )

  const openWalletAssist = (): void => {
    const url = state.value.walletAssist?.resumeUrl
    if (!url || typeof window === 'undefined' || !wallets.isAllowedRedirect(url)) return
    window.location.href = url
  }
  watch(tonWallet.isConnecting, (value) => {
    walletHandlers.isTonBusy.value = value
  })

  return {
    ...amount,
    ...actions,
    ...copy,
    ...walletHandlers,
    availableSourceTokens,
    canCancelExecution: computed(() => canCancelExecution.value || canAbandonExecution.value),
    routeLocked,
    routeEditLocked,
    networkSelectionLocked,
    cardClass,
    copyDebugLog,
    debugCopyStatus,
    debugLog,
    disconnectBtnBorderClass: 'border-gray-200 bg-white shadow-sm',
    disconnectBtnClass,
    inputBgClass,
    isLoadingTokens,
    maskedTonAddress,
    needsTonBackendReauth,
    needsTonReconnect: wallets.needsTonReconnect,
    openWalletAssist,
    phantomDeeplinkAvailable,
    promptPaddingClass,
    routeUxStatus,
    selectChain,
    selectedChainId,
    selectedSourceTokenAddress,
    selectedToken,
    solanaBridgeProvider,
    sourceAmount,
    sourceChainOptions,
    sourceChainType,
    sourceWalletAddress,
    state,
    tokenLoadBlockedMessage,
    uiError,
    continueLabel: primaryContinueLabel,
    isContinueDisabled: primaryDisabled,
    isFlowBusy: primaryBusy,
    handleContinue: handlePrimaryContinue,
    handleAmountInput: completedRouteHandlers.handleAmountInput,
    handlePhantomBridgeFallback,
    showPhantomBridgeFallback,
    debouncedRequote: buy.debouncedRequote,
    externalActionButton: computed(() => props.externalActionButton),
    selectToken: completedRouteHandlers.selectToken,
    setMaxAmount: completedRouteHandlers.setMaxAmount,
    applyQuickAmount: completedRouteHandlers.applyQuickAmount,
  }
}
